import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Grid } from "@mui/material";
import { addDays, format } from "date-fns";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import TitledBox from "../../../components/TitledBox";
import { CargosList } from "../../../components/cargo-parts/CargosList";
import { LoadingPartRanges } from "../../../components/cargo-parts/LoadingPartRanges";
import { ParametersPart } from "../../../components/cargo-parts/ParametersPart";
import { SalesPart } from "../../../components/cargo-parts/SalesPart";
import { UnloadingPartRanges } from "../../../components/cargo-parts/UnloadingPartRanges";
import AddCargoSkeleton from "../../../components/skeletons/AddCargoSkeleton";
import { useAiQualityTool } from "../../../context/AiQualityToolContext";
import { useCargosTable } from "../../../context/CargoTableContext";
import { useAddViaCompanion } from "../../../hooks/use-add-via-companion";
import { useErrorNotifications } from "../../../hooks/useErrorsNotification";
import { useSettings } from "../../../hooks/useSettings";
import { compareRawAndAppendedData } from "../../../utils/aiQualityTool.helpers";
import { useAuth } from "../../auth/AuthContext";
import { useGetIntegrationStatusQuery } from "../../integrations/integrationsApiSlice";
import { PostalCodesMixer } from "../PostalCodesMixer";
import { RelationSwap } from "../RelationSwap";
import {
	useAddCargoMutation,
	useSendAIAccuracyMutation,
} from "../cargoApiSlice";
import { getFormSchema } from "../cargosFormSchema";
import { getFormSchema as getWithTransSchema } from "../cargosFormSchemaWithCustomTrans";
import { defaultValues } from "../constants";
import { DrawerControl } from "./DrawerControl";
import { parseTimesToDate } from "./utils";

export const FormGrid = ({
	onClose,
	shouldHideDrawer,
	drawerData,
	handleRemoveBox,
}) => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const hasCustomTransConnection = user?.hasCustomTransConnection;
	const { qualityCheck, setResult } = useAiQualityTool();
	const { pagination, setPagination } = useCargosTable();
	const { enqueueSnackbar } = useSnackbar();
	const { settings } = useSettings();
	const { data: integrationsStatuses } = useGetIntegrationStatusQuery();
	const { addViaCompanion } = useAddViaCompanion();
	const [addCargo, { isLoading: isAddingCargos }] = useAddCargoMutation();

	// no loading state needed for this mutation
	const [sendAccuracy] = useSendAIAccuracyMutation();

	const [rowErrors, setRowErrors] = useState([]);
	const [postalMixCounter, setPostalMixCounter] = useState(0);
	const [loadingAddressDefaultValue, setLoadingAddressDefaultValue] =
		useState(null);
	const [unloadingAddressDefaultValue, setUnloadingAddressDefaultValue] =
		useState(null);

	const formSchema = hasCustomTransConnection
		? getWithTransSchema(t)
		: getFormSchema(t);
	const {
		handleSubmit,
		control,
		formState: { errors, isSubmitSuccessful },
		setValue,
		getValues,
		reset,
		resetField,
		watch,
	} = useForm({
		resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	const {
		fields: cargos,
		append: appendCargo,
		remove: removeCargo,
	} = useFieldArray({
		control,
		name: "cargos",
	});

	useErrorNotifications(errors);

	function handleAppend(cargoDetails) {
		const {
			id,
			raw,
			cargos,
			mailingGroups_1,
			mailingGroups_2,
			mailingGroups_3,
			price,
			customerPrice,
			publicNote,
			privateNote,
			payment,
			loadingRepeat,
			mailingToGroups,
			shouldPostOnStock,
			shouldPostAfter,
			shouldRefreshOffer,
			autoSearchFreeCars,
			gatherPriceProposals,
			isFtl,
			loadingAddressCustomCity,
			loadingAddressCustomCountry,
			loadingAddressCustomPostalCode,
			unloadingAddressCustomCity,
			unloadingAddressCustomCountry,
			unloadingAddressCustomPostalCode,
			loadingDateType,
			unloadingDateType,
			shouldBePublic,
			...details
		} = cargoDetails;

		// quality tool
		// raw will be available only from AI boxes
		// manual or copy added cargos will not have raw data
		if (raw) {
			const cleanedRawData = Object.fromEntries(
				Object.entries(raw).filter(
					([_, v]) =>
						v !== "" &&
						v !== null &&
						v !== undefined &&
						v !== 0 &&
						!(Array.isArray(v) && v.length === 0),
				),
			);
			const returnedFields = Object.keys(cleanedRawData).length;
			const changedFields = compareRawAndAppendedData(raw, cargoDetails);

			setResult({ returnedFields, changedFields });
		}
		// end quality tool

		const mailingGroupsIds = [
			mailingGroups_1,
			mailingGroups_2,
			mailingGroups_3,
		].filter(Boolean);

		const append = (repeatIndex = 0) => {
			const cargoRowDetails = {
				...details,
				...(price && { price }),
				...(customerPrice && { customerPrice }),
				...(publicNote && { publicNote }),
				...(privateNote && { privateNote }),
				...(payment && { payment: `${payment}` }),

				...(loadingAddressCustomCity && { loadingAddressCustomCity }),
				...(loadingAddressCustomCountry && { loadingAddressCustomCountry }),
				...(loadingAddressCustomPostalCode && {
					loadingAddressCustomPostalCode,
				}),
				...(unloadingAddressCustomCity && { unloadingAddressCustomCity }),
				...(unloadingAddressCustomCountry && { unloadingAddressCustomCountry }),
				...(unloadingAddressCustomPostalCode && {
					unloadingAddressCustomPostalCode,
				}),
				isOriginal: repeatIndex === 0 ? true : false,
				loadingRepeat: loadingRepeat || 0,
				mailingToGroups: mailingToGroups || false,
				mailingGroupsIds,
				shouldPostOnStock: shouldPostOnStock || false,
				...(shouldPostAfter && {
					shouldPostAfter: `${shouldPostAfter}`,
				}),
				shouldRefreshOffer: shouldRefreshOffer || false,
				autoSearchFreeCars: autoSearchFreeCars || false,
				shouldBePublic: shouldBePublic || false,
				gatherPriceProposals: gatherPriceProposals || false,
				isFtl: isFtl === "true" ? true : false,
				...(loadingDateType === "day" && {
					loadingDate: repeatIndex
						? addDays(new Date(details.loadingDate), repeatIndex)
						: new Date(details.loadingDate),
				}),
				...(unloadingDateType === "day" && {
					unloadingDate: repeatIndex
						? addDays(new Date(details.unloadingDate), repeatIndex)
						: new Date(details.unloadingDate),
				}),
				loadingDateType,
				unloadingDateType,
				source: raw ? "AI" : "MANUAL",
				originalLoadingAddress: addressBeforeMix.loading,
				originalUnloadingAddress: addressBeforeMix.unloading,
			};
			appendCargo(cargoRowDetails);
			setPostalMixCounter(0);
			setAddressBeforeMix({
				loading: null,
				unloading: null,
			});
			// remove box from proposition list
			handleRemoveBox(id);
		};

		// Initial append
		append();

		// Handle repeats
		if (loadingRepeat) {
			for (let index = 1; index <= loadingRepeat; index++) {
				append(index);
			}
		}

		// clear default values
		setLoadingAddressDefaultValue(null);
		setUnloadingAddressDefaultValue(null);
	}

	async function handleSubmitCargos() {
		const { cargos } = getValues();

		const parsedCargos = cargos.map(
			({
				cargos,
				loadingAddress,
				loadingDate,
				loadingDateRange,
				unloadingAddress,
				unloadingDate,
				unloadingDateRange,
				...rest
			}) => {
				return {
					...rest,
					loadingAddress: loadingAddress?.id,
					...(loadingDate &&
						rest.loadingDateType === "day" && {
							loadingDate: format(loadingDate, "yyyy-MM-dd"),
						}),
					...(loadingDateRange &&
						rest.loadingDateType === "range" && {
							loadingDateRange: loadingDateRange.map((date) => {
								return format(date, "yyyy-MM-dd");
							}),
						}),
					unloadingAddress: unloadingAddress?.id,
					...(unloadingDate &&
						rest.unloadingDateType === "day" && {
							unloadingDate: format(unloadingDate, "yyyy-MM-dd"),
						}),
					...(unloadingDateRange &&
						rest.unloadingDateType === "range" && {
							unloadingDateRange: unloadingDateRange.map((date) => {
								return format(date, "yyyy-MM-dd");
							}),
						}),
				};
			},
		);

		setRowErrors([]);

		await addCargo(parsedCargos)
			.unwrap()
			.then(async (payload) => {
				// companion app
				if (
					payload.some((cargo) => cargo.postOnTrans) ||
					payload.some((cargo) => cargo.postOnTimocom)
				) {
					const data = {
						trans: payload.filter((cargo) => cargo.postOnTrans),
						timocom: payload.filter((cargo) => cargo.postOnTimocom),
					};
					addViaCompanion(data);
				}

				// common success handling
				const word = cargos.length > 1 ? t("cargos") : t("cargo");
				enqueueSnackbar(`${t("added")} ${word}.`, { variant: "success" });
				setResetAutocompleteKey(Date.now());
				reset({
					cargos: [],
					...defaultValues,
				});
				setPagination({
					...pagination,
					pageIndex: 0,
				});

				// close modal if no more propositions available
				if (drawerData.length === 0) {
					onClose();
				}

				// send AI quality analysis
				const { jobId, expectedFields, returnedFields, changedFields } =
					qualityCheck;

				await sendAccuracy({
					jobId,
					data: {
						expectedFields,
						returnedFields,
						changedFields,
					},
				}).unwrap();
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						const splittedKey = key.split(".");
						const parsedMessage = message[0].replace(key, splittedKey[2]);
						const errorIndex = Number(splittedKey[1]);
						setRowErrors((prevRowErrors) => [...prevRowErrors, errorIndex]);
						enqueueSnackbar(parsedMessage, {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	}

	// clear fields after append
	const [resetAutocompleteKey, setResetAutocompleteKey] = useState();
	useEffect(() => {
		if (isSubmitSuccessful) {
			setResetAutocompleteKey(Date.now());
			reset({
				cargos,
				...defaultValues,
				shouldPostOnStock:
					integrationsStatuses.transEuStatus ||
					integrationsStatuses.timocomStatus ||
					integrationsStatuses.telerouteStatus ||
					true,
				postOnTrans: hasCustomTransConnection
					? integrationsStatuses.transEuStatus
					: true,
				postOnTimocom: integrationsStatuses.timocomStatus,
				postOnTeleroute: integrationsStatuses.telerouteStatus,
				shouldPostAfter:
					integrationsStatuses?.timocomStatus ||
					integrationsStatuses?.telerouteStatus
						? "0"
						: null,
				shouldRefreshOffer:
					integrationsStatuses.transEuStatus ||
					integrationsStatuses.timocomStatus ||
					integrationsStatuses.telerouteStatus,
				gatherPriceProposals: integrationsStatuses.timocomStatus,
				autoSearchFreeCars: integrationsStatuses.timocomStatus,
				...(hasCustomTransConnection && { cargoTypeId: "PALETTE" }),
			});
		}
	}, [isSubmitSuccessful, reset, cargos]);

	const handleUseCargoData = (data) => {
		const {
			id,
			loadingAddressCity,
			loadingAddressCountry,
			loadingAddressZip,
			loadingDate,
			loadingTime,
			unloadingAddressCity,
			unloadingAddressCountry,
			unloadingAddressZip,
			unloadingDate,
			unloadingTime,
			customerPrice,
			ftl,
			length,
			weight,
			vehicleTypeId,
			vehicleBodyId,
			additionalRequirements,
			raw,
		} = data;

		// quality tool
		setValue("raw", raw);
		// end quality tool

		setValue("id", id);

		setResetAutocompleteKey(Date.now());
		setValue("loadingAddress", "");
		setValue("unloadingAddress", "");

		// timeout to wait for autocomplete reset
		setTimeout(() => {
			// ftl / ltl
			if (ftl) {
				setValue(
					"isFtl",
					ftl?.data?.toLowerCase() === "ftl" ? "true" : "false",
				);
			}

			// check loading type based on date
			const loadingDateType = loadingDate.data.includes(":") ? "range" : "day";
			setValue("loadingDateType", loadingDateType);

			// check unloading type based on date
			const unloadingDateType = unloadingDate.includes(":") ? "range" : "day";
			setValue("unloadingDateType", unloadingDateType);

			setValue("loadingAddressCountry", loadingAddressCountry);
			setValue("loadingAddressCustomPostalCode", loadingAddressZip);
			setValue("loadingAddressCustomCity", loadingAddressCity);
			setValue("loadingAddressCustomCountry", loadingAddressCountry);

			if (loadingDateType === "day") {
				setValue("loadingDate", loadingDate.data);
			}
			if (loadingDateType === "range") {
				setValue(
					"loadingDateRange",
					loadingDate.data.split(":").map((d) => new Date(d)),
				);
			}

			const parsedLoadingTime = loadingTime?.data?.split("-") || [];
			if (parsedLoadingTime[0]) {
				setValue("loadingTimeStart", parseTimesToDate(parsedLoadingTime[0]));
			}
			if (parsedLoadingTime[1]) {
				setValue("loadingTimeEnd", parseTimesToDate(parsedLoadingTime[1]));
			}

			setValue("unloadingAddressCountry", unloadingAddressCountry);
			setValue("unloadingAddressCustomPostalCode", unloadingAddressZip);
			setValue("unloadingAddressCustomCity", unloadingAddressCity);
			setValue("unloadingAddressCustomCountry", unloadingAddressCountry);

			if (unloadingDateType === "day") {
				setValue("unloadingDate", unloadingDate);
			}
			if (unloadingDateType === "range") {
				setValue(
					"unloadingDateRange",
					unloadingDate.split(":").map((d) => new Date(d)),
				);
			}

			const parsedUnloadingTime = unloadingTime?.data?.split("-") || [];
			if (parsedUnloadingTime[0]) {
				setValue(
					"unloadingTimeStart",
					parseTimesToDate(parsedUnloadingTime[0]),
				);
			}
			if (parsedUnloadingTime[1]) {
				setValue("unloadingTimeEnd", parseTimesToDate(parsedUnloadingTime[1]));
			}

			setLoadingAddressDefaultValue({
				address: {
					postalCode: loadingAddressZip,
					city: loadingAddressCity,
				},
				selectFirst: true,
			});

			setUnloadingAddressDefaultValue({
				address: {
					postalCode: unloadingAddressZip,
					city: unloadingAddressCity,
				},
				selectFirst: true,
			});

			setValue("weight", weight.data);
			setValue("length", length.data);

			if (customerPrice > 0) {
				setValue("customerPrice", customerPrice);
			}

			setValue(
				"vehicleTypeId",
				Array.isArray(vehicleTypeId.data)
					? vehicleTypeId.data
					: [vehicleTypeId.data],
			);
			setValue(
				"vehicleBodyId",
				Array.isArray(vehicleBodyId.data)
					? vehicleBodyId.data
					: [vehicleBodyId.data],
			);
			additionalRequirements &&
				setValue("additionalRequirements", additionalRequirements);

			// Sales support fields
			setValue(
				"shouldPostOnStock",
				integrationsStatuses.transEuStatus ||
					integrationsStatuses.timocomStatus ||
					integrationsStatuses.telerouteStatus ||
					true,
			);
			setValue(
				"postOnTrans",
				hasCustomTransConnection ? integrationsStatuses.transEuStatus : true,
			);
			setValue("postOnTimocom", integrationsStatuses.timocomStatus);
			setValue("postOnTeleroute", integrationsStatuses.telerouteStatus);
			setValue(
				"shouldPostAfter",
				integrationsStatuses?.timocomStatus ||
					integrationsStatuses?.telerouteStatus
					? "0"
					: null,
			);
			setValue(
				"shouldRefreshOffer",
				integrationsStatuses.transEuStatus ||
					integrationsStatuses.timocomStatus ||
					integrationsStatuses.telerouteStatus,
			);
			setValue("gatherPriceProposals", integrationsStatuses.timocomStatus);
			setValue("autoSearchFreeCars", integrationsStatuses.timocomStatus);

			if (hasCustomTransConnection) {
				setValue("cargoTypeId", data?.cargoTypeId || "PALETTE", {
					shouldValidate: true,
				});
			}
		}, 300);
	};

	// handle copy cargo from already added cargos
	function handleCopyCargo(cargo) {
		const loadingDateType = cargo.loadingDateType;
		const unloadingDateType = cargo.unloadingDateType;

		// copy date types
		setValue("loadingDateType", loadingDateType, {
			shouldValidate: true,
		});
		setValue("unloadingDateType", unloadingDateType, {
			shouldValidate: true,
		});

		// copy loading date
		if (loadingDateType === "day") {
			setValue("loadingDate", cargo.loadingDate, {
				shouldValidate: true,
			});
		}
		if (loadingDateType === "range") {
			setValue("loadingDateRange", cargo.loadingDateRange, {
				shouldValidate: true,
			});
		}

		// copy unloading date
		if (unloadingDateType === "day") {
			setValue("unloadingDate", cargo.unloadingDate, {
				shouldValidate: true,
			});
		}
		if (unloadingDateType === "range") {
			setValue("unloadingDateRange", cargo.unloadingDateRange, {
				shouldValidate: true,
			});
		}

		// copy times
		setValue("loadingTimeStart", cargo.loadingTimeStart);
		setValue("loadingTimeEnd", cargo.loadingTimeEnd);
		setValue("unloadingTimeStart", cargo.unloadingTimeStart);
		setValue("unloadingTimeEnd", cargo.unloadingTimeEnd);

		setValue("loadingAddressCountry", cargo.loadingAddressCountry, {
			shouldValidate: true,
		});
		if (cargo.loadingAddress) {
			setValue("loadingAddress", cargo.loadingAddress, {
				shouldValidate: true,
			});
			setLoadingAddressDefaultValue(cargo.loadingAddress);
		} else {
			const addressObj = {
				title: `${cargo.loadingAddressCustomCountry},${cargo.loadingAddressCustomPostalCode}, ${cargo.loadingAddressCustomCity}`,
				address: {
					label: `${cargo.loadingAddressCustomCountry},${cargo.loadingAddressCustomPostalCode}, ${cargo.loadingAddressCustomCity}`,
					countryCode: cargo.loadingAddressCustomCountry,
					city: cargo.loadingAddressCustomCity,
					postalCode: cargo.loadingAddressCustomPostalCode,
				},
			};
			setValue("loadingAddress", addressObj, {
				shouldValidate: true,
			});
			setLoadingAddressDefaultValue(addressObj);
			setValue(
				"loadingAddressCustomCountry",
				cargo.loadingAddressCustomCountry,
				{
					shouldValidate: true,
				},
			);
			setValue(
				"loadingAddressCustomPostalCode",
				cargo.loadingAddressCustomPostalCode,
				{
					shouldValidate: true,
				},
			);
			setValue("loadingAddressCustomCity", cargo.loadingAddressCustomCity, {
				shouldValidate: true,
			});
		}

		setValue("loadingRepeat", cargo.loadingRepeat, {
			shouldValidate: true,
		});
		setValue("privateNote", cargo.privateNote, {
			shouldValidate: true,
		});

		setValue("unloadingAddressCountry", cargo.unloadingAddressCountry, {
			shouldValidate: true,
		});
		if (cargo.unloadingAddress) {
			setValue("unloadingAddress", cargo.unloadingAddress, {
				shouldValidate: true,
			});
			setUnloadingAddressDefaultValue(cargo.unloadingAddress);
		} else {
			const addressObj = {
				title: `${cargo.unloadingAddressCustomCountry},${cargo.unloadingAddressCustomPostalCode}, ${cargo.unloadingAddressCustomCity}`,
				address: {
					label: `${cargo.unloadingAddressCustomCountry},${cargo.unloadingAddressCustomPostalCode}, ${cargo.unloadingAddressCustomCity}`,
					countryCode: cargo.unloadingAddressCustomCountry,
					city: cargo.unloadingAddressCustomCity,
					postalCode: cargo.unloadingAddressCustomPostalCode,
				},
			};
			setValue("unloadingAddress", addressObj, {
				shouldValidate: true,
			});
			setUnloadingAddressDefaultValue(addressObj);
			setValue(
				"unloadingAddressCustomCountry",
				cargo.unloadingAddressCustomCountry,
				{
					shouldValidate: true,
				},
			);
			setValue(
				"unloadingAddressCustomPostalCode",
				cargo.unloadingAddressCustomPostalCode,
				{
					shouldValidate: true,
				},
			);
			setValue("unloadingAddressCustomCity", cargo.unloadingAddressCustomCity, {
				shouldValidate: true,
			});
		}

		setValue("weight", cargo.weight, {
			shouldValidate: true,
		});
		setValue("length", cargo.length, {
			shouldValidate: true,
		});
		setValue(
			"vehicleTypeId",
			Array.isArray(cargo.vehicleTypeId)
				? cargo.vehicleTypeId
				: [cargo.vehicleTypeId],
			{
				shouldValidate: true,
			},
		);
		setValue("vehicleBodyId", cargo.vehicleBodyId, {
			shouldValidate: true,
		});
		setValue("price", cargo.price, {
			shouldValidate: true,
		});
		setValue("customerPrice", cargo.customerPrice, {
			shouldValidate: true,
		});
		setValue("publicNote", cargo.publicNote, {
			shouldValidate: true,
		});
		setValue("payment", cargo.payment, {
			shouldValidate: true,
		});
		setValue("mailingToGroups", cargo.mailingToGroups, {
			shouldValidate: true,
		});
		setValue("mailingGroups_1", cargo.mailingGroupsIds[0], {
			shouldValidate: true,
		});
		setValue("mailingGroups_2", cargo.mailingGroupsIds[1], {
			shouldValidate: true,
		});
		setValue("mailingGroups_3", cargo.mailingGroupsIds[2], {
			shouldValidate: true,
		});
		setValue("shouldPostOnStock", cargo.shouldPostOnStock, {
			shouldValidate: true,
		});
		setValue(
			"shouldPostAfter",
			cargo.shouldPostAfter === "10"
				? "10"
				: cargo.shouldPostAfter === "20"
					? "20"
					: cargo.shouldPostOnStock
						? "0"
						: null,
			{
				shouldValidate: true,
			},
		);
		setValue("shouldRefreshOffer", cargo.shouldRefreshOffer, {
			shouldValidate: true,
		});
		setValue("postOnTrans", cargo.postOnTrans, {
			shouldValidate: true,
		});
		setValue("postOnTimocom", cargo.postOnTimocom, {
			shouldValidate: true,
		});
		setValue("postOnTeleroute", cargo.postOnTeleroute, {
			shouldValidate: true,
		});
		setValue("autoSearchFreeCars", cargo.autoSearchFreeCars, {
			shouldValidate: true,
		});
		setValue("shouldBePublic", cargo?.shouldBePublic || false, {
			shouldValidate: true,
		});
		setValue("gatherPriceProposals", cargo.gatherPriceProposals, {
			shouldValidate: true,
		});
		setValue("isFtl", cargo.isFtl ? "true" : "false", {
			shouldValidate: true,
		});

		setValue("additionalRequirements", cargo.additionalRequirements, {
			shouldValidate: true,
		});
	}

	const [addressBeforeMix, setAddressBeforeMix] = useState({
		loading: null,
		unloading: null,
	});

	return (
		<Grid container columnGap={0}>
			<Grid
				size={{
					lg: 12,
				}}
			>
				<Box sx={{ padding: "30px" }}>
					{!settings && <AddCargoSkeleton />}
					{settings && (
						<>
							<Box
								component="form"
								noValidate
								onSubmit={handleSubmit(handleAppend)}
							>
								<Box sx={{ display: "none" }}>
									<Controller
										control={control}
										name="id"
										render={({ field: { onChange, value } }) => (
											<input type="hidden" onChange={onChange} value={value} />
										)}
									/>
									<Controller
										control={control}
										name="raw"
										render={({ field: { onChange, value } }) => (
											<input type="hidden" onChange={onChange} value={value} />
										)}
									/>
								</Box>
								<Grid container rowSpacing={3.6} columnSpacing={2}>
									<Grid
										size={{
											lg: 6.85,
										}}
									>
										<TitledBox
											title={t("load")}
											color="#7f7f7f"
											sx={{ width: "100%", height: "100%" }}
										>
											<LoadingPartRanges
												control={control}
												watch={watch}
												errors={errors}
												setValue={setValue}
												resetField={resetField}
												resetAutocompleteKey={resetAutocompleteKey}
												addressDefaultValue={loadingAddressDefaultValue}
											/>
										</TitledBox>
									</Grid>
									<Grid
										size={{
											lg: 0.3,
										}}
									>
										<Box
											sx={{
												height: "100%",
												display: "flex",
												flexDirection: "column",
												justifyContent: "center",
												alignItems: "center",
												gap: "14px",
												position: "relative",
												zIndex: 10,
											}}
										>
											<RelationSwap
												watch={watch}
												setValue={setValue}
												setLoadingAddressDefaultValue={
													setLoadingAddressDefaultValue
												}
												setUnloadingAddressDefaultValue={
													setUnloadingAddressDefaultValue
												}
											/>
											<PostalCodesMixer
												watch={watch}
												setValue={setValue}
												counter={postalMixCounter}
												setCounter={setPostalMixCounter}
												setAddressBeforeMix={setAddressBeforeMix}
											/>
										</Box>
									</Grid>
									<Grid
										size={{
											lg: 4.85,
										}}
									>
										<TitledBox
											title={t("unload")}
											color="#7f7f7f"
											sx={{ width: "100%", height: "100%" }}
										>
											<UnloadingPartRanges
												control={control}
												errors={errors}
												setValue={setValue}
												watch={watch}
												resetAutocompleteKey={resetAutocompleteKey}
												addressDefaultValue={unloadingAddressDefaultValue}
											/>
										</TitledBox>
									</Grid>
									<Grid
										size={{
											lg: 6,
										}}
									>
										<TitledBox
											title={t("cargo_parameters_and_vehicle_requirements")}
											color="#0069b4"
											sx={{
												width: "100%",
												height: "100%",
												position: "relative",
											}}
										>
											<ParametersPart
												control={control}
												errors={errors}
												setValue={setValue}
												getValues={getValues}
												watch={watch}
												settings={settings}
											/>
										</TitledBox>
									</Grid>
									<Grid
										size={{
											lg: 6,
										}}
									>
										<TitledBox
											title={t("sales_support")}
											color="#63c29a"
											sx={{
												width: "100%",
												height: "100%",
												"& .MuiCheckbox-root": { py: 0 },
											}}
										>
											<SalesPart
												control={control}
												errors={errors}
												setValue={setValue}
												getValues={getValues}
												resetField={resetField}
												settings={settings}
												watch={watch}
											/>
										</TitledBox>
									</Grid>
									<Grid
										size={{
											lg: 6,
										}}
									>
										<Box sx={{ display: "flex", justifyContent: "end" }}>
											<Button
												type="submit"
												variant="contained"
												color="secondary"
											>
												{t("add_to_queue")}
											</Button>
										</Box>
									</Grid>
									<Grid
										size={{
											lg: 6,
										}}
									>
										<Box sx={{ display: "flex", justifyContent: "start" }}>
											<Button
												onClick={handleSubmitCargos}
												loading={isAddingCargos}
												variant="contained"
												disabled={cargos.length < 1}
											>
												{t("send")}
											</Button>
										</Box>
									</Grid>

									{cargos.length > 0 && (
										<CargosList
											settings={settings}
											cargos={cargos}
											rowErrors={rowErrors}
											handleRemoveRow={removeCargo}
											handleCopyCargo={handleCopyCargo}
										/>
									)}
								</Grid>
							</Box>
							<DrawerControl
								data={drawerData}
								handleUseData={handleUseCargoData}
								handleRemoveBox={handleRemoveBox}
								shouldHideDrawer={shouldHideDrawer}
							/>
						</>
					)}
				</Box>
			</Grid>
		</Grid>
	);
};
